import { Component, HostListener, Input, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FroelingHistoryPoint } from '../version.service';
import { chartPalette, timeTooltipTitle, uiLocale, lineLegendLabels, dragZoom, setpointColor, heatingCircuitColor, exhaustColor, alignedTimeTicks, visibleTimeTick } from '../shared/chart.utils';

@Component({
  selector: 'app-heizung-chart',
  template: `
    <!-- .dash-modal-chart (styles.css): 55 vh in the desktop modal, the free height of the phone sheet -->
    <div class="dash-modal-chart">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              (click)="resetZoom()">{{ 'CHART_RESET' | translate }}</button>
    </div>
  `,
})
export class HeizungChartComponent implements OnChanges, OnDestroy {
  // The series' labels and the axis titles are translated in TS: rebuild on a language switch.
  private langSub: Subscription;

  constructor(private translate: TranslateService, private readonly zone: NgZone) {
    this.langSub = translate.onLangChange.subscribe(() => this.ngOnChanges());
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }

  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  /** Deep-link id of the chart (the modal's view path, 'heizung/chart'): series selection and zoom
   *  window go into the link (shared/chart-link.plugin.ts); null = not linked. */
  @Input() linkId: string | null = null;
  @Input() history: FroelingHistoryPoint[] = [];
  @Input() darkMode = false;
  @Input() windowMs = 24 * 3600 * 1000;

  zoomed = false;
  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};
  /** Phone (< 576 px, the modal is a full-screen sheet): compact legend and tooltip, fewer ticks, no axis titles. */
  private phone = window.innerWidth < 576;

  /** Crossing the phone breakpoint rebuilds the options (a linked zoom window is restored by the plugin). */
  @HostListener('window:resize')
  onResize(): void {
    if ((window.innerWidth < 576) !== this.phone) this.ngOnChanges();
  }

  /** Reset button: chartjs-plugin-zoom reports the reset to onZoomComplete, which drops the link's window. */
  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  /** After a drag zoom, a reset and a restored link window (Chart.js runs outside Angular). */
  private onZoomComplete(): void {
    this.zone.run(() => { this.zoomed = !!(this.chartDirective?.chart as any)?.isZoomedOrPanned?.(); });
  }

  /** A heating circuit's name in the UI language, as on its card (HeizungComponent.hcLabel):
   *  "Heizkreis 01" → "Heating Circuit 01"; a name without a number as reported. */
  private hcLabel(name: string): string {
    const n = name.match(/(\d+)/)?.[1];
    return n ? `${this.translate.instant('HEIZKREIS')} ${n}` : name;
  }

  /** Tooltip title: the time, beyond a day's window also the date (a week shows every time seven times);
   *  the UI locale is read at hover time, so it follows a language switch. */
  private tooltipTitle(): (items: any[]) => string {
    if (this.windowMs <= 26 * 3600 * 1000) return timeTooltipTitle;
    return (items) => {
      const ts = items[0]?.parsed.x;
      return ts == null ? '' : new Date(ts).toLocaleString(uiLocale(), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };
  }

  ngOnChanges(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const t = (k: string, p?: any): string => this.translate.instant(k, p);
    const phone = this.phone = window.innerWidth < 576;
    const tick = phone ? { font: { size: 10 } } : {};
    const pts = this.history;
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;
    // New options replace the scales: the zoom is gone until the link plugin restores its window.
    this.zoomed = false;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        // phone: up to a dozen series — small legend items, a tooltip that leaves the chart visible
        legend: { display: true, labels: { ...lineLegendLabels(textColor), ...(phone ? { padding: 6, font: { size: 11 } } : {}) } },
        tooltip: {
          ...(phone ? { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 6, boxWidth: 8, boxHeight: 8 } : {}),
          callbacks: {
            title: this.tooltipTitle(),
            label: (item) => {
              if ((item.dataset as any).yAxisID === 'y1') {
                return ` ${item.dataset.label}: ${Math.round(item.parsed.y ?? 0)} %`;
              }
              return ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} °C`;
            },
          },
        },
        zoom: dragZoom(() => this.onZoomComplete()),
        ...({ deepLink: { id: this.linkId } } as any),
      },
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          // ticks and their format from the visible window (a drag zoom, a restored link window), not
          // the full range: whole hours / days / months, thinned by autoSkip to maxTicksLimit
          afterBuildTicks: (scale: any) => {
            const t = alignedTimeTicks(scale.min, scale.max);
            if (t.length >= 2) scale.ticks = t.map(value => ({ value }));
          },
          ticks: {
            ...tick,
            color: textColor,
            maxTicksLimit: phone ? 4 : 9,
            maxRotation: 0,
            autoSkipPadding: 12,
            callback: function (this: any, val: number | string) { return visibleTimeTick(this.min, this.max, val); },
          },
          grid: { color: gridColor },
        },
        // `resolution` (yMinSpan plugin, shared/chart-link.plugin.ts): the Fröling reports whole degrees
        // (the tooltip's decimal only shows in the archive's bucket averages), so a flat line keeps
        // at least 10 °C of axis instead of magnifying a 1 °C step.
        y: {
          resolution: 1,
          ticks: {
            ...tick,
            color: textColor,
            ...(phone ? { maxTicksLimit: 5 } : {}),
            callback: (val: number | string) => `${Number.isInteger(val) ? val : (val as number).toFixed(1)} °C`,
          },
          grid: { color: gridColor },
          title: { display: !phone, text: t('HEIZUNG_AXIS_TEMP'), color: textColor, font: { size: 11 } },
        } as any,
        y1: {
          resolution: 1,   // whole %
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            ...tick,
            color: textColor,
            callback: (val: number | string) => `${val} %`,
            maxTicksLimit: phone ? 5 : 6,
          },
          grid: { drawOnChartArea: false },
          title: { display: !phone, text: t('HEIZUNG_AXIS_SOC'), color: textColor, font: { size: 11 } },
        } as any,
      },
      elements: {
        point: { radius: 0, hoverRadius: 3 },
        line: { borderWidth: 1.5, tension: 0.6 },
      },
    };

    // linkKey: the dataset's stable name in the deep link (labels are translated, optional series shift indices)
    const datasets: any[] = [
      {
        label: `🔥 ${t('HEIZUNG_KESSEL')}`,
        linkKey: 'boilerTempC',
        data: pts.map(p => ({ x: p.ts, y: p.boilerTempC })),
        borderColor: '#dc3545',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: `🛢️ ${t('HEIZUNG_BUFFER_TOP_LONG')}`,
        linkKey: 'bufferTopTempC',
        data: pts.map(p => ({ x: p.ts, y: p.bufferTopTempC })),
        borderColor: '#0d6efd',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: `🛢️ ${t('HEIZUNG_BUFFER_BOTTOM_LONG')}`,
        linkKey: 'bufferBottomTempC',
        data: pts.map(p => ({ x: p.ts, y: p.bufferBottomTempC })),
        borderColor: '#6ea8fe',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [4, 3],
      },
      {
        label: `🚿 ${t('HEIZUNG_WARMWASSER')}`,
        linkKey: 'hotWaterTempC',
        data: pts.map(p => ({ x: p.ts, y: p.hotWaterTempC })),
        borderColor: '#0dcaf0',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: `🌤️ ${t('HEIZUNG_OUTDOOR_SHORT')}`,
        linkKey: 'outdoorTempC',
        data: pts.filter(p => p.outdoorTempC != null).map(p => ({ x: p.ts, y: p.outdoorTempC })),
        borderColor: '#6c757d',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [6, 4],
      },
    ];

    if (pts.some(p => p.solarThermalTempC > 0)) {
      datasets.push({
        label: `☀️ ${t('HEIZUNG_SOLAR')}`,
        linkKey: 'solarThermalTempC',
        data: pts.map(p => ({ x: p.ts, y: p.solarThermalTempC })),
        borderColor: '#ffc107',
        backgroundColor: 'transparent',
        fill: false,
      });
    }

    if (pts.some(p => p.exhaustTempC > 0)) {
      datasets.push({
        label: `💨 ${t('HEIZUNG_ABGAS')}`,
        linkKey: 'exhaustTempC',
        data: pts.map(p => ({ x: p.ts, y: p.exhaustTempC })),
        borderColor: exhaustColor(this.darkMode),   // solid brown: dashed orange means Soll
        backgroundColor: 'transparent',
        fill: false,
      });
    }

    // Set-point (Soll) lines: the set-point orange for every circuit, like on the cards and in the
    // circuit popups; the circuits are told apart by dash pattern (the legend swatch shows it) and label.
    // The first circuit's [5, 4] is the popup's Soll dash.
    const sollColor = setpointColor(this.darkMode);
    const sollDashes: number[][] = [[5, 4], [2, 3], [8, 3, 2, 3], [1, 3]];
    for (let i = 0; i < (pts[0]?.heatingCircuits?.length ?? 0); i++) {
      const name = pts[0].heatingCircuits[i]?.name ?? `HK${i + 1}`;
      const label = this.hcLabel(name);
      const color = heatingCircuitColor(i, this.darkMode);
      datasets.push({
        label: `🌡️ ${label}`,
        linkKey: `hc:${name}`,
        data: pts.filter(p => p.heatingCircuits?.[i]).map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
        borderColor: color,
        backgroundColor: 'transparent',
        fill: false,
      });
      // Flow set-point (Fröling app): in the set-point colour, with the circuit's dash (see sollDashes). Same
      // points as the actual line (index tooltips pair them by array index), null — a gap — where unknown.
      const target = pts.filter(p => p.heatingCircuits?.[i]).map(p => {
        const t = p.heatingCircuits[i].flowTempTargetC ?? 0;
        return { x: p.ts, y: t > 0 ? t : null };
      });
      if (target.some(d => d.y != null)) {
        datasets.push({
          label: `🌡️ ${t('HEIZUNG_HK_SOLL_LINE', { name: label })}`,
          linkKey: `hcSoll:${name}`,
          data: target,
          borderColor: sollColor,
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.5,   // as the popup's expanded Soll line: the dotted patterns stay readable
          borderDash: sollDashes[i % sollDashes.length],
        });
      }
    }

    datasets.push({
      label: `🛢️ ${t('HEIZUNG_BUFFER_PCT')}`,
      linkKey: 'bufferChargePct',
      data: pts.map(p => ({ x: p.ts, y: p.bufferChargePct })),
      borderColor: '#a855f7',
      backgroundColor: 'transparent',
      fill: false,
      yAxisID: 'y1',
      borderDash: [4, 3],
    });

    this.chartData = { datasets };
  }
}
